import React, { useState, useEffect } from 'react';
import { Event } from '../types';
import EventCard from './EventCard';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface FeaturedEventsCarouselProps {
  events: Event[];
  onEventClick: (event: Event) => void; 
}

const FeaturedEventsCarousel: React.FC<FeaturedEventsCarouselProps> = ({ events, onEventClick }) => {
  const { isAuthenticated } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const totalPages = Math.ceil(events.length / 3); 
  
  const nextSlide = () => {
    if (isAnimating) return;
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  useEffect(() => {
    if (events.length <= 3) return; 
    
    const timer = setInterval(() => {
      if (!isAnimating) {
        nextSlide();
      }
    }, 5000);
    
    return () => clearInterval(timer);
  }, [events.length, isAnimating]);

  if (events.length === 0) return null;

  const currentEvents = events.slice(currentIndex * 3, (currentIndex * 3) + 3);

  return (
    <div className="relative pb-12 overflow-hidden rounded-xl"> 
      <div className="overflow-visible relative">
        <AnimatePresence initial={false} mode="wait" custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            initial={{ x: direction > 0 ? 1000 : -1000, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction < 0 ? 1000 : -1000, opacity: 0 }}
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
            className="w-full"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"> 
              <AnimatePresence>
                {currentEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                      delay: index * 0.2
                    }}
                  >
                    <EventCard
                      event={event}
                      onClick={() => onEventClick(event)}
                      index={index}
                      isFeatured={true}
                      hideCreator={!isAuthenticated}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </AnimatePresence>
      </div> {/* Carousel container */}

      {events.length > 3 && ( 
        <>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={prevSlide}
            className="absolute left-0 sm:-left-4 top-1/2 -translate-y-1/2 bg-theme-background text-theme-text-main p-2 sm:p-3 rounded-full hover:bg-theme-cards transition-colors z-10 shadow-lg border border-theme-cards min-h-[40px] min-w-[40px] flex items-center justify-center" /* Background and border colors for navigation buttons */
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={nextSlide} /* Background and border colors for navigation buttons */
            className="absolute right-0 sm:-right-4 top-1/2 -translate-y-1/2 bg-theme-background text-theme-text-main p-2 sm:p-3 rounded-full hover:bg-theme-cards transition-colors z-10 shadow-lg border border-theme-cards min-h-[40px] min-w-[40px] flex items-center justify-center"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </motion.button>
        </>
      )}

      {events.length > 3 && ( 
        <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1 sm:gap-2">
          {Array.from({ length: totalPages }).map((_, index) => (
            <motion.button
              key={index}
              onClick={() => {
                setDirection(index > currentIndex ? 1 : -1);
                setCurrentIndex(index);
              }} /* Primary and cards colors for pagination dots */
              className={`h-2 rounded-full transition-all duration-300 ${
                currentIndex === index 
                  ? 'bg-theme-primary w-8' 
                  : 'bg-theme-cards w-2 hover:bg-theme-background'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
          <h2 className="text-xl font-semibold text-contrast">Featured Vybes</h2>
        </div>
      )}
    </div>
  );
};

export default FeaturedEventsCarousel;